import { getPerformanceReviewInvitationPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import { ControlledPerformanceReviewInvitationFields } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewInvitationFields.tsx";
import {
  copyCommonInvitationToEmptyReviewerTypes,
  createPerformanceReviewInvitationSchemaFields,
  PerformanceReviewEditPageStep,
} from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import {
  PerformanceReviewEditPageProps,
  usePerformanceReviewEditPage,
} from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import { usePerformanceReviewUpdateInvitation } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import {
  ApiPerformanceReviewInvitationMode,
  ApiPerformanceReviewUpdateInvitation,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, FullScreenPage, Select, Stack, Switch, Title } from "@skailer-com/uikit";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export const PerformanceReviewEditInvitationPage = ({ review }: PerformanceReviewEditPageProps) => {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { confirmExit, navigateToStep } = usePerformanceReviewEditPage();

  const referenceOrder = PerformanceReviewReviewerTypeItems().map((item) => item.id);
  const reviewerTypes = review.reviewer_types.slice().sort((a, b) => {
    const safeIndex = (index: number) => (index === -1 ? 999 : index);
    return safeIndex(referenceOrder.indexOf(a.type)) - safeIndex(referenceOrder.indexOf(b.type));
  });

  const schema = yup.object({
    invitation_from_employee_id: yup.number().required(t("errors:field_required")),
    invitation_mode: yup
      .mixed<ApiPerformanceReviewInvitationMode>()
      .oneOf(Object.values(ApiPerformanceReviewInvitationMode))
      .required(t("errors:field_required")),
    ...createPerformanceReviewInvitationSchemaFields(t("errors:field_required")),
    reviewer_types: yup
      .array()
      .of(
        yup.object({
          id: yup.number().required(t("errors:field_required")),
          ...createPerformanceReviewInvitationSchemaFields(t("errors:field_required")),
        })
      )
      .required(),
  });

  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ApiPerformanceReviewUpdateInvitation>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      invitation_from_employee_id: review.invitation_from_employee.id,
      invitation_mode: review.invitation_mode,
      invitation_subject: review.invitation_subject,
      invitation_body: review.invitation_body,
      reviewer_types: reviewerTypes.map((reviewerType) => ({
        id: reviewerType.id,
        invitation_subject: reviewerType.invitation_subject,
        invitation_body: reviewerType.invitation_body,
      })),
    },
  });

  const invitationMode = useWatch({ control, name: "invitation_mode" });
  const invitationFromOptions = [review.invitation_from_employee, ...review.owners]
    .filter(
      (employee, index, employees) =>
        employees.findIndex((item) => item.id === employee.id) === index
    )
    .map((employee) => ({ id: employee.id, title: renderEmployeeName(employee) }));

  const setInvitationsByReviewerType = (isEnabled: boolean) => {
    if (isEnabled) {
      setValue(
        "reviewer_types",
        copyCommonInvitationToEmptyReviewerTypes(getValues("reviewer_types"), {
          invitation_subject: getValues("invitation_subject"),
          invitation_body: getValues("invitation_body"),
        }),
        { shouldDirty: true }
      );
    }

    setValue(
      "invitation_mode",
      isEnabled
        ? ApiPerformanceReviewInvitationMode.by_reviewer_type
        : ApiPerformanceReviewInvitationMode.common,
      { shouldDirty: true }
    );
  };

  const updateInvitation = usePerformanceReviewUpdateInvitation(control);
  const onSubmit = handleSubmit(async (payload) => {
    try {
      await updateInvitation.mutateAsync({ id: review.id, payload });
      navigateToStep(review.id, PerformanceReviewEditPageStep.ratings);
    } catch {}
  });

  return (
    <FullScreenPage
      backSlot={
        <Button variant="secondary" size="lg" onClick={() => confirmExit(review.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => navigateToStep(review.id, PerformanceReviewEditPageStep.settings)}
          >
            {t("common:back")}
          </Button>
          <Button size="lg" onClick={onSubmit} isLoading={updateInvitation.isPending}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={
        <PerformanceReviewTitleSlot
          step={PerformanceReviewEditPageStep.invitation}
          review={review}
        />
      }
      isCenterVertical={false}
      sidebarSlot={
        <PerformanceReviewEditSidebar
          step={PerformanceReviewEditPageStep.invitation}
          review={review}
        />
      }
    >
      <Stack gap="sm" justify="center">
        <Stack className="card-panel" gap="2xl">
          <Title header={t("performance_review:invitation_settings")} size="lg" />
          <Stack gap={"md"}>
            <Controller
              control={control}
              name="invitation_from_employee_id"
              render={({ field }) => (
                <Select
                  label={t("common:invite_from")}
                  emptyTitle={t("common:select")}
                  value={field.value}
                  options={invitationFromOptions}
                  onChange={field.onChange}
                  error={errors.invitation_from_employee_id?.message}
                  required
                />
              )}
            />
            <Switch
              checked={invitationMode === ApiPerformanceReviewInvitationMode.by_reviewer_type}
              onChange={setInvitationsByReviewerType}
              title={t("performance_review:configure_invitations_by_reviewer_type")}
            />
          </Stack>
        </Stack>

        {invitationMode === ApiPerformanceReviewInvitationMode.common ? (
          <Stack className="card-panel" gap="xl">
            <Title
              header={t("common:invitation")}
              caption={t("performance_review:reviewer_type_invitation_description")}
              paddingBottom
            />
            <ControlledPerformanceReviewInvitationFields
              control={control}
              subjectName="invitation_subject"
              bodyName="invitation_body"
              subjectError={errors.invitation_subject?.message}
              bodyError={errors.invitation_body?.message}
              variableOptions={getPerformanceReviewInvitationPlaceholderOptions()}
            />
          </Stack>
        ) : (
          reviewerTypes.map((reviewerType, index) => (
            <Stack key={reviewerType.id} gap="2xl" className="card-panel">
              <Title
                header={
                  PerformanceReviewReviewerTypeItems().find((item) => item.id === reviewerType.type)
                    ?.title ??
                  reviewerType.title ??
                  ""
                }
                caption={t("performance_review:reviewer_type_invitation_description")}
                paddingBottom
              />
              <ControlledPerformanceReviewInvitationFields
                control={control}
                subjectName={`reviewer_types.${index}.invitation_subject`}
                bodyName={`reviewer_types.${index}.invitation_body`}
                subjectError={errors.reviewer_types?.[index]?.invitation_subject?.message}
                bodyError={errors.reviewer_types?.[index]?.invitation_body?.message}
                variableOptions={getPerformanceReviewInvitationPlaceholderOptions()}
              />
            </Stack>
          ))
        )}

        <Button size="lg" onClick={onSubmit} isLoading={updateInvitation.isPending}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
